import { AdditionalDetailsData, AdyenCheckout, AdyenCheckoutError, ApplePay, CheckoutAdvancedFlowResponse, CoreConfiguration, GooglePay, ICore, ResultCode, SubmitActions, SubmitData, UIElement, UIElementProps } from '@adyen/adyen-web';
import { IssueLogger } from '../../Pages/Root/deferred/issueLogger';
import { FormSubmitToggle } from '../../Pages/Root/FormSubmitToggle';
import { I3ds2Response } from '../../Unbundled/IComponentState';

type AdyenEnvironment = "test" | "live";

type ExpressPayClickHandler = (
    resolve: () => void,
    reject: () => void
) => void;

interface IShippingPostal {
    billingCurrency: string;
    countryCode2: string;
    postalCode: string;
    shippingMethod: string;
}

export interface IAdyenClientData {
    action: any;
    order: any;
    resultCode: ResultCode;
}

interface GooglePayError {
    code: GooglePayErrorCode;
    message: string;
}

/**
 * Copied from the Adyen Google error codes which are not exported.
 * Typescript will not allow us to compile if we add one which is not
 * supported by Adyen internally, but we do need to repeat them here as we cannot inherit.
 */
type GooglePayErrorCode =
    | "SHIPPING_ADDRESS_INVALID"
    | "SHIPPING_ADDRESS_UNSERVICEABLE"
    | "SHIPPING_OPTION_INVALID"
    | "OFFER_INVALID"
    | "PAYMENT_DATA_INVALID"
    | "OTHER_ERROR";

/**
 * Copied from the Adyen Google intent codes which are not exported.
 */
type CallbackIntent = "OFFER" | "SHIPPING_ADDRESS" | "SHIPPING_OPTION" | "PAYMENT_AUTHORIZATION" | "PAYMENT_METHOD";

type ErrorReason =
    | "SHIPPING_ADDRESS_INVALID"
    | "SHIPPING_ADDRESS_UNSERVICEABLE"
    | "SHIPPING_OPTION_INVALID"
    | "OFFER_INVALID"
    | "PAYMENT_DATA_INVALID"
    | "OTHER_ERROR";

interface IExpressPayPaymentResponse<TError> {
    clientData: IAdyenClientData;
    idempotencyKey: string;
    errors?: TError[];
    mainRedirectUrl: string;
    success: boolean;
}

interface IGoogleAddress {
    street?: string,
    houseNumberOrName?: string,
    postalCode?: string,
    city?: string,
    stateOrProvince?: string,
    country?: string,
    firstName?: string,
    lastName?: string,
}

interface IGooglePayPaymentResponse extends IExpressPayPaymentResponse<GooglePayError> { }
interface IApplePayPaymentResponse extends IExpressPayPaymentResponse<ApplePayError> { }

/**
 * The shape of a shipping method returned from our server for Express payment.
 */
interface IExpressPayShippingMethod {
    amount: string,     // To two decimal places, eg "1.23". Not used by all Express providers.
    detail: string;     // For example, "Arrives in 5 to 7 days".
    identifier: string; // Server side ID.
    label: string;      // "Free Shipping".
    selected?: boolean; // Should only be set on one of the shipping options.
}

interface IExpressPayShippingUpdateBase<TError, TLineItem> {
    countryCode2?: string;
    countryCode3?: string;
    data: IExpressPayShippingUpdateData<TError, TLineItem>;
}

interface IExpressPayShippingUpdateData<TError, TLineItem> {
    errors?: TError[];
    newLineItems?: TLineItem[];
    newShippingMethods?: IExpressPayShippingMethod[];
    newTotal: TLineItem;
}

interface IApplePayShippingUpdate extends IExpressPayShippingUpdateBase<ApplePayError, ApplePayJS.ApplePayLineItem> { }
interface IGooglePayShippingUpdate extends IExpressPayShippingUpdateBase<GooglePayError, GooglePayLineItem> { }

interface ExpressShippingMethodUpdateResponse<TError, TLineItem> {
    newLineItems?: TLineItem[];
    newTotal: TLineItem;
}

interface IApplePayShippingMethodUpdate extends ExpressShippingMethodUpdateResponse<ApplePayError, ApplePayJS.ApplePayLineItem> { }
interface IGooglePayShippingMethodUpdate extends ExpressShippingMethodUpdateResponse<GooglePayError, GooglePayLineItem> { }

interface GooglePayLineItem {
    amount: string;
    label: string;
    type: GoogleLineItemType;
}

type GoogleLineItemType = "LINE_ITEM" | "SUBTOTAL" | "TAX" | "DISCOUNT" | "SHIPPING_OPTION";

export class ExpressPay {
    private applePayComponent: ApplePay;
    private applePayData: any;
    private checkout: ICore;
    private googlePayData: any;
    private googlePayComponent: GooglePay;
    private idempotencyKey: string;
    private shippingAddress: IShippingPostal;
    private verificationContainerId = "verification-container";
    private verificationComponent: UIElement;

    public static async InitAsync(pageValidationCallback: ExpressPayClickHandler, formSelector: string): Promise<ExpressPay> {
        let expressPay = new ExpressPay(pageValidationCallback, formSelector);

        expressPay.idempotencyKey = expressPay.readFieldValue("IdempotencyKey");

        if (!expressPay.idempotencyKey) { // Key will be empty if the basket is empty.
            return expressPay;
        }

        let adyenConfiguration = expressPay.loadAdyenConfiguration();
        expressPay.checkout = await AdyenCheckout(adyenConfiguration);

        expressPay.resetExpressPay();

        return expressPay;
    }

    constructor(private readonly pageValidationCallback: ExpressPayClickHandler, private readonly formSelector: string) {
    }

    private loadAdyenConfiguration(): CoreConfiguration {
        let paymentMethods = JSON.parse(this.readFieldValue("AdyenConfiguration_PaymentMethods"));

        let adyenConfiguration: CoreConfiguration = {
            clientKey: this.readFieldValue("AdyenConfiguration_ClientKey"),
            countryCode: this.countryCode2(),
            environment: this.readFieldValue("AdyenConfiguration_Environment") as AdyenEnvironment,
            locale: this.readFieldValue("AdyenConfiguration_CustomerLocale"),
            onError: (error: AdyenCheckoutError, component: UIElement) => {
                const componentJson = JSON.stringify(component);
                const messageText = error.message || "No message";
                const causeText = error.cause ? error.cause.toString() : "No cause";
                const message = `Adyen Express Pay basket top level error - ${causeText} / ${messageText} from component '${componentJson}'.`;
                IssueLogger.log(message, "Adyen", error);
            },
            onAdditionalDetails: (state, component, actions) => { this.handleOnAdditionalDetails(state, component, actions); },
            paymentMethodsResponse: paymentMethods,
        };

        return adyenConfiguration;
    }

    private countryCode2(): string {
        return this.readFieldValue("AdyenConfiguration_CountryCode2");
    }

    private readFieldValue(fieldId: string): string {
        let element = (<HTMLInputElement>document.getElementById(fieldId));

        if (element) {
            return element.value;
        }

        return "";
    }

    public resetExpressPay(): void {
        let total = parseInt(this.readFieldValue("BasketTotals_TotalInUnits"));
        let subTotal = this.readFieldValue("BasketTotals_SubTotalAsDecimal");
        let deliveryTotal = this.readFieldValue("BasketTotals_DeliveryAsDecimal");
        let deliveryServicesAsJson = this.readFieldValue("DeliveryServicesAsJson");
        let currencyIsoCode = this.currencyIsoCode;
        let merchantName = this.readFieldValue("AdyenConfiguration_MerchantName");
        let isStoreDelivery = (this.readFieldValue("DeliveryServices_IsStoreDelivery").toLowerCase() === "true");
        let shippingMethods = JSON.parse(deliveryServicesAsJson) as IExpressPayShippingMethod[];
        let shippingType: ApplePayJS.ApplePayShippingType = isStoreDelivery ? 'storePickup' : 'delivery';

        if (!shippingMethods || shippingMethods.length === 0) {
            return;
        }

        const selected = shippingMethods.find(sm => sm.selected);

        // Load shipping from cart, otherwise may not be correct for non UK if delivery type changed in Apple UI.
        this.shippingAddress = {
            billingCurrency: this.currencyIsoCode,
            countryCode2: this.readFieldValue("AddressCountryCode2"),
            postalCode: this.readFieldValue("AddressPostalCode"),
            shippingMethod: selected?.identifier,
        }

        this.configureApplePay(this.checkout, total, subTotal, deliveryTotal, 0, shippingMethods, merchantName, shippingType, currencyIsoCode);

        let totalInUnits = this.readFieldValue("BasketTotals_TotalAsDecimal");

        this.configureGooglePay(this.checkout, totalInUnits, subTotal, deliveryTotal, shippingMethods);
    }

    private configureGooglePay(checkout: ICore, total: string, subTotal: string, deliveryTotal: string, shippingMethods: IExpressPayShippingMethod[]): void {
        let googlePayContainer = document.getElementById("google-button-container");

        if (!googlePayContainer) {
            // Google Pay UI does not exist, possibly not enabled for this locale / site or a single use voucher has been added to the basket.
            return;
        }

        if (this.googlePayComponent) {
            this.googlePayComponent.unmount();
        }

        this.googlePayComponent = new GooglePay(checkout, {
            billingAddressRequired: true,
            billingAddressParameters: { format: "FULL", phoneNumberRequired: true },
            buttonColor: "black",
            buttonSizeMode: "fill",
            callbackIntents: ["SHIPPING_ADDRESS", "SHIPPING_OPTION", "PAYMENT_AUTHORIZATION", "PAYMENT_METHOD"],
            configuration: {
                gatewayMerchantId: this.readFieldValue("AdyenConfiguration_MerchantAccount"),
                merchantId: this.readFieldValue("AdyenConfiguration_GoogleMerchantId"),
                merchantName: this.readFieldValue("AdyenConfiguration_MerchantName"),
            },
            countryCode: this.countryCode2(),
            emailRequired: true,
            environment: this.readFieldValue("AdyenConfiguration_GoogleEnvironment"),
            isExpress: true,
            onAuthorized: ({ authorizedEvent, billingAddress, deliveryAddress }, actions) => {
                this.onGooglePayAuthorised(actions, authorizedEvent, billingAddress, deliveryAddress);
            },
            onClick: this.pageValidationCallback,
            onSubmit: (state, component, actions) => this.onGooglePaySubmit(state, component, actions),
            paymentDataCallbacks: {
                onPaymentDataChanged: paymentData => this.onGooglePayPaymentDataChanged(paymentData),
            },
            shippingAddressRequired: true,
            shippingAddressParameters: {
                allowedCountryCodes: [],
                phoneNumberRequired: true
            },
            shippingOptionRequired: true,
            shippingOptionParameters: this.mapShippingToGoogle(shippingMethods),

            transactionInfo: {
                displayItems: [
                    { label: "Sub total", type: "SUBTOTAL", price: subTotal },
                    { label: "Delivery", type: "SHIPPING_OPTION", price: deliveryTotal },
                ],
                countryCode: this.countryCode2(),
                currencyCode: this.currencyIsoCode,
                totalPriceStatus: 'FINAL',
                totalPrice: total,
                totalPriceLabel: 'Total'
            }
        });

        this.googlePayComponent
            .isAvailable()
            .then(() => {
                this.showAndMountComponent(this.googlePayComponent, "#google-button-container", "payment-select-googlepay");
                this.googlePayComponent.mount("#google-button-container");
            })
            .catch(error => {
                if (error.message.indexOf("is not available on this device") < 0) {
                    IssueLogger.log("Google Pay failed to mount", "GooglePay", error.toString());
                }
            });
    }

    private onGooglePayAuthorised(actions: { resolve: () => void; reject: (error?: string | google.payments.api.PaymentDataError) => void; },
        event: google.payments.api.PaymentData, billingAddress: IGoogleAddress, deliveryAddress: IGoogleAddress): void {

        // Although we are passed the billing and delivery addresses as distinct parameters, we don't need to save them as they 
        // exist within the event shipping address and billing info with additional contact fields.
        this.googlePayData = {
            billingCurrency: this.currencyIsoCode,
            email: event.email,
            shippingAddress: event.shippingAddress,
            shippingMethod: event.shippingOptionData.id,
            token: event.paymentMethodData,
        };

        actions.resolve();
    }

    /**
     * Converts a list of express shipping methods into the format required by Google Pay.
     * 
     * See - https://developers.google.com/pay/api/web/reference/request-objects#ShippingOptionParameters 
     * @param shippingMethods
     * @returns
     */
    private mapShippingToGoogle(shippingMethods: IExpressPayShippingMethod[]): google.payments.api.ShippingOptionParameters {

        const selectedMethod = shippingMethods.find(method => method.selected);

        return {
            defaultSelectedOptionId: selectedMethod?.identifier,
            shippingOptions: shippingMethods.map((method) => {
                return {
                    description: method.detail,
                    id: method.identifier,
                    label: method.label,
                }
            })
        };
    }

    private async onGooglePayPaymentDataChanged(paymentData: google.payments.api.IntermediatePaymentData)
        : Promise<google.payments.api.PaymentDataRequestUpdate> {
        const { callbackTrigger, shippingAddress, shippingOptionData } = paymentData;

        // If shipping options or transaction info need updating:
        if (callbackTrigger === 'INITIALIZE' || callbackTrigger === 'SHIPPING_ADDRESS') {
            return await this.onGooglePayShippingAddressUpdated(shippingAddress);
        }
        else if (callbackTrigger === 'SHIPPING_OPTION') {
            return await this.onGooglePayShippingMethodSelected(shippingAddress, shippingOptionData.id);
        }

        IssueLogger.log(`Received invalid callback trigger of '${callbackTrigger}'`, "onGooglePayPaymentDataChanged");

        return {};
    }

    private async onGooglePayShippingMethodSelected(address: google.payments.api.IntermediateAddress, shippingMethod: string)
        : Promise<google.payments.api.PaymentDataRequestUpdate> {
        try {
            this.shippingAddress = {
                billingCurrency: this.currencyIsoCode,
                countryCode2: address.countryCode,
                postalCode: address.postalCode,
                shippingMethod: shippingMethod,
            };

            const json = JSON.stringify(this.shippingAddress);

            const response = await fetch("/ws/update-express-shipping-method", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: json
            });

            if (!response?.ok) {
                // Http error, tell Adyen that we don't support this shipping method.
                IssueLogger.log("Rejecting due to network error.", "onGooglePayShippingMethodSelected", response.statusText);
                return this.googleNetworkError("SHIPPING_OPTION", "SHIPPING_OPTION_INVALID");
            }

            const shippingMethodUpdate: IGooglePayShippingMethodUpdate = await response.json();
            return this.mapShippingMethodUpdateToGoogle(shippingMethodUpdate);
        } catch (error: unknown) {
            IssueLogger.log("onGooglePayShippingMethodSelected", "Express-Pay", error);
            return this.googleNetworkError("SHIPPING_OPTION", "OTHER_ERROR");
        }
    }

    private async onGooglePayShippingAddressUpdated(address: google.payments.api.IntermediateAddress): Promise<google.payments.api.PaymentDataRequestUpdate> {

        try {
            this.shippingAddress = {
                billingCurrency: this.currencyIsoCode,
                countryCode2: address.countryCode,
                postalCode: address.postalCode,
                shippingMethod: this.shippingAddress.shippingMethod, // Use the same method, server may replace services and try to keep it selected.
            };
            let response = await fetch("/ws/update-express-shipping-address", {
                method: "post",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(this.shippingAddress)
            });

            if (!response?.ok) {
                // Http error, tell Adyen that we don't support this shipping destination.
                IssueLogger.log("Rejecting due to network error.", "onGooglePayShippingUpdateRequest", response);
                return this.googleNetworkError("SHIPPING_ADDRESS", "SHIPPING_ADDRESS_UNSERVICEABLE");
            }

            let shippingUpdate: IGooglePayShippingUpdate = await response.json();
            return this.mapShippingUpdateToGoogle(shippingUpdate);
        } catch (error: unknown) {
            IssueLogger.log("onGooglePayShippingUpdateRequest", "Express-Pay", error);
            return this.googleNetworkError("SHIPPING_ADDRESS", "OTHER_ERROR");
        }
    }

    private mapShippingUpdateToGoogle(update: IGooglePayShippingUpdate): google.payments.api.PaymentDataRequestUpdate {
        return {
            error: this.mapGoogleError(update),
            newShippingOptionParameters: this.mapShippingToGoogle(update.data.newShippingMethods),
            newTransactionInfo: {
                countryCode: update.countryCode2,
                currencyCode: this.currencyIsoCode,
                displayItems: update.data.newLineItems.map(item => {
                    return {
                        label: item.label,
                        type: "LINE_ITEM",
                        price: item.amount,
                    }
                }),
                totalPriceStatus: "FINAL",
                totalPrice: update.data.newTotal.amount,
                totalPriceLabel: "Total"
            },
        };
    }

    /**
     * Extracts and returns the first error from the server validation.
     * We may have multiple errors on an order but Google only accepts one for
     * display.
     * @param update
     * @returns
     */
    private mapGoogleError(update: IGooglePayShippingUpdate): google.payments.api.PaymentDataError {

        if (update.data.errors && update.data.errors.length > 0) {
            return {
                reason: update.data.errors[0].code,
                message: update.data.errors[0].message,
                intent: "SHIPPING_ADDRESS"
            };
        }

        return null;
    }

    private mapShippingMethodUpdateToGoogle(shippingMethodUpdate: IGooglePayShippingMethodUpdate): google.payments.api.PaymentDataRequestUpdate {
        return {
            newTransactionInfo: {
                countryCode: this.countryCode2(),
                currencyCode: this.currencyIsoCode,
                displayItems: shippingMethodUpdate.newLineItems.map(item => {
                    return {
                        label: item.label,
                        type: "SHIPPING_OPTION",
                        price: item.amount,
                    }
                }),
                totalPriceStatus: "FINAL",
                totalPrice: shippingMethodUpdate.newTotal.amount,
                totalPriceLabel: "Total"
            },
        };
    }

    private get currencyIsoCode(): string {
        return this.readFieldValue("BasketTotals_CurrencyIsoCode");
    }

    private configureApplePay(checkout: ICore, total: number, subTotal: string, delivery: string,
        amountFromGiftCard: number, shippingMethods: IExpressPayShippingMethod[],
        merchantName: string, shippingType: ApplePayJS.ApplePayShippingType, currencyIsoCode: string): void {
        let applePayContainer = document.getElementById("applepay-button-container");

        if (!applePayContainer) {
            // Apple Pay UI does not exist, possibly not enabled for this locale / site or a single use voucher has been added to the basket.
            return;
        }

        let lineItems = [];

        lineItems.push({ label: "Items", "amount": subTotal });
        lineItems.push({ label: "Delivery", "amount": delivery });
        if (amountFromGiftCard) {
            lineItems.push({ label: "Gift card", "amount": "-" + amountFromGiftCard.toFixed(2).toString() });
        }

        if (this.applePayComponent) {
            this.applePayComponent.unmount();
        }

        let requiredShippingContactFields: ApplePayJS.ApplePayContactField[] = ["name", "phone", "email"];
        // Only require the shipping fields if we are not delivering to store. Apple Pay then hides the shipping address selection UI.
        if (shippingType !== 'storePickup') {
            requiredShippingContactFields.push("postalAddress");
        }

        this.applePayComponent = new ApplePay(checkout, {
            amount: {
                currency: currencyIsoCode,
                value: total
            },
            buttonColor: "black",
            buttonType: "plain",
            isExpress: true,
            totalPriceLabel: merchantName,
            lineItems: lineItems,

            onAdditionalDetails: (state, component, actions) => actions.resolve({ resultCode: 'Authorised' }),

            onAuthorized: ({ authorizedEvent, billingAddress, deliveryAddress }, actions) => {
                // Called just before onSubmit. When using an Apple Pay Express the addresses can
                // be checked here and rejected if they are not valid. We currently check the addresses
                // in the submit function so we just resolve it as OK for now.
                this.onApplePayAuthorised(actions, authorizedEvent)
            },

            // Called when the shopper clicks the Apple Pay button. Pass the resolve() and reject() actions
            // to a callback that will choose to continue or stop the payment flow depending on page validation.
            onClick: this.pageValidationCallback,

            onError: (error: AdyenCheckoutError) => {
                if (error.name !== "CANCEL") { // Cancel is normal for dismissing the payment sheet, not an error.
                    IssueLogger.log("Apple Pay error", "ApplePay", error);
                }
            },

            onShippingContactSelected: (
                resolve: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
                reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
                event: ApplePayJS.ApplePayShippingContactSelectedEvent) => this.onApplePayShippingAddressUpdated(resolve, reject, event),

            /*
            * @param resolve(ApplePayShippingMethodUpdate update) Completes the selection of a shipping method with an update. Will call https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778024-completeshippingmethodselection
            * @param reject(ApplePayShippingMethodUpdate update)
            * @param event The event parameter contains the shippingMethod attribute.
            */
            onShippingMethodSelected: (
                resolve: (update: ApplePayJS.ApplePayShippingMethodUpdate) => void,
                reject: (update: ApplePayJS.ApplePayShippingMethodUpdate) => void,
                event: ApplePayJS.ApplePayShippingMethodSelectedEvent) => this.onApplePayShippingMethodSelected(resolve, reject, event),

            onSubmit: (state, component, actions) => this.onApplePaySubmit(state, component, actions),

            // We'd like to request more fields here, but Apple restricts billing to just address and name.
            requiredBillingContactFields: ["postalAddress"],
            requiredShippingContactFields: requiredShippingContactFields,
            // Updated whenever country is changed. Loads from initial data on page load.
            shippingMethods: shippingMethods,
            shippingType: shippingType
        });

        this.applePayComponent
            .isAvailable()
            .then(() => {
                this.showAndMountComponent(this.applePayComponent, "#applepay-button-container", "payment-select-applepay");
            })
            .catch(error => {
                if (error.message.indexOf("is not available on this device") < 0) {
                    IssueLogger.log("Apple Pay (express) failed to mount", "ApplePay", error.toString());
                }
            });
    }

    private showAndMountComponent(component: UIElement, componentSelector: string, tickSelector: string): void {
        // Show the '--or--' divider that separates the payment buttons.
        document.querySelectorAll(".or-hr").forEach(element => {
            element.classList.remove("hidden");
        });

        component.mount(componentSelector);

        const tickElement = document.getElementById(tickSelector);

        // Not sure about this. Express is not used on the final /payment page and the tick is only used there. Is this needed any more?
        // Might be of benefit if we merge the script for the two pages.
        if (tickElement) {
            tickElement.classList.remove("hidden");
        }
    }

    public async onGooglePaySubmit(state: SubmitData, component: UIElement<UIElementProps>, actions: SubmitActions): Promise<void> {
        try {
            if (state.isValid) {
                let formData = {
                    data: this.googlePayData,
                    paymentData: state.data,
                    idempotencyKey: this.idempotencyKey,
                };

                let json = JSON.stringify(formData);
                const response = await fetch("/checkout/process-google-pay", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: json
                });

                if (response.ok) {
                    const data: IGooglePayPaymentResponse = await response.json();
                    this.onGooglePayExpressCompletePaymentResponseReceived(data, actions);
                } else {
                    const data = await response.json();
                    this.postError("onGooglePaySubmit", data, "Google");

                    const error = this.createGooglePayAuthError("Error", "PAYMENT_AUTHORIZATION",
                        "PAYMENT_DATA_INVALID", "We received an error when processing your payment.");
                    actions.reject(error);
                }
                FormSubmitToggle.DisableForm(this.formSelector);
            }
        } catch (error: unknown) {
            this.postError("onGooglePaySubmit-error", error, "Google");
            const googleError = this.createGooglePayAuthError("Error", "PAYMENT_AUTHORIZATION",
                "PAYMENT_DATA_INVALID", "We received an error when processing your payment.");
            actions.reject(googleError);
        }
    }

    public async onApplePaySubmit(state: SubmitData, component: UIElement<UIElementProps>, actions: SubmitActions): Promise<void> {
        try {
            if (state.isValid) {
                let formData = {
                    applePayData: this.applePayData,
                    idempotencyKey: this.idempotencyKey,
                };

                let json = JSON.stringify(formData);

                const response = await fetch("/checkout/process-apple-pay", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: json
                });

                if (response.ok) {
                    const data: IApplePayPaymentResponse = await response.json();
                    this.onCompletePaymentResponseReceived(data, actions);
                } else {
                    const data = await response.json();
                    this.postError("onApplePaySubmit", data, "Apple");
                    actions.reject();
                }
                FormSubmitToggle.DisableForm(this.formSelector);
            }
        } catch (error: unknown) {
            this.postError("onApplePaySubmit-error", error, "Apple");
            actions.reject();
        }
    }

    private onApplePayAuthorised(
        actions: any,
        event: ApplePayJS.ApplePayPaymentAuthorizedEvent
    ): void {
        this.applePayData = {
            billingContact: event.payment.billingContact,
            billingCurrency: this.currencyIsoCode,
            shippingContact: event.payment.shippingContact,
            shippingMethod: this.shippingAddress.shippingMethod,
            token: JSON.stringify(event.payment.token.paymentData),
        };

        actions.resolve();
    }

    /**
     * Called when the user selects a shipping address.
     * 
     * The Adyen definition of reject does not have parameters, but the Apple Pay definition does so this routine errs on the side of caution.
     * If we call reject without passing parameters it seems to complain that the object passed does not have newTotals.
     * The Apple docs talk of passing error messages so it seems that reject should receive data.
     * 
     * See - https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingcontactupdate
     * 
     * @param resolve
     * @param reject
     * @param event
     * @returns
     */
    private async onApplePayShippingAddressUpdated(resolve: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
        reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
        event: ApplePayJS.ApplePayShippingContactSelectedEvent): Promise<void> {

        let alreadyRejected = false; // So we don't reject multiple times on network errors.

        try {
            this.shippingAddress = {
                billingCurrency: this.currencyIsoCode,
                countryCode2: event.shippingContact.countryCode,
                postalCode: event.shippingContact.postalCode,
                shippingMethod: this.shippingAddress.shippingMethod, // Use the same method, server may replace services and try to keep it selected.
            };
            let response = await fetch('/ws/update-express-shipping-address', {
                method: 'post',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify(this.shippingAddress)
            });

            if (!response?.ok) {
                // Http error, tell Adyen that we don't support this shipping destination.
                // Also, don't log the whole response as it may cause Safari to throw an exception if it cannot be serialised.
                alreadyRejected = true;
                IssueLogger.log("Rejecting due to network error.", "onApplePayShippingContactSelected", response?.status);
                this.rejectShippingContact(reject);
                return;
            }

            let shippingUpdate: IApplePayShippingUpdate = await response.json();
            let update = shippingUpdate.data;

            if (update.errors && update.errors.length > 0) {
                const applePayUpdate: ApplePayJS.ApplePayShippingContactUpdate = {
                    errors: this.createAppleUiErrors(update.errors),
                    newTotal: update.newTotal,
                };

                // Log the reason why we are rejecting so we can narrow down any bad rejections. Will be noisy so will need filtering over time.
                IssueLogger.log("Apple Pay shipping contact rejected.", "onApplePayShippingContactSelected", JSON.stringify(update));
                alreadyRejected = true;
                reject(applePayUpdate);
            } else {
                update.errors = [];
                resolve(update);
            }
        } catch (error: unknown) {
            if (!alreadyRejected) { // A rejection above may cause an exception, so don't reject or log again.
                IssueLogger.log("onApplePayShippingContactSelected", "Express-Pay", error);
                this.rejectShippingContact(reject);
            }
        }
    }

    /**
     * A belt and braces approach to rejecting an Apple shipping contact update when we don't have the
     * data for the rejection. For example, when we receive a network error.
     * 
     * We need to send these fields or we generate more logs but mostly they are just placeholders.
     * @param reject
     */
    private rejectShippingContact(reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void): void {
        // We have to cast this after creation as the type definition insists on a newTotal field, but passing
        // that as 0 allows the order to go through.
        const applePayUpdate = {
            errors: this.createAppleUiErrors([{ code: "unknown", message: "Error with shipping contact" }])
        } as ApplePayJS.ApplePayShippingContactUpdate;

        return reject(applePayUpdate);
    }

    /**
     * Converts the server errors into Apple UI errors.
     * @param serverErrors
     * @returns A list of filtered errors for the client.
     */
    private createAppleUiErrors(serverErrors: ApplePayError[]): ApplePayError[] {
        let appleErrors = [];

        // If we don't return errors then status is assumed to be success (see https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingcontactupdate).
        // ApplePayError is a class, not a simple list of properties so must be created in this fashion. See https://developer.apple.com/forums/thread/92912
        for (const err of serverErrors) {
            if (err.code) { // Some errors are from our server and don't have a code.
                appleErrors.push(new ApplePayError(err.code, err.contactField, err.message));
            }
        }

        return appleErrors;
    }

    /**
     * Similar to onShippingContactSelected, this method passes data to the reject call which is not specified in the Adyen
     * type definitions. This is required for Apple Pay to handle errors correctly.
     * 
     * See - https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingmethodupdate
     * @param resolve
     * @param reject
     * @param event
     */
    private async onApplePayShippingMethodSelected(
        resolve: (update: ApplePayJS.ApplePayShippingMethodUpdate) => void,
        reject: (update: ApplePayJS.ApplePayShippingMethodUpdate) => void,
        event: ApplePayJS.ApplePayShippingMethodSelectedEvent
    ): Promise<void> {
        let alreadyRejected = false; // So we don't reject multiple times on network errors.

        try {
            // Save the shipping method for the final authorise call so we don't need to store it in the cart.
            this.shippingAddress.shippingMethod = event.shippingMethod.identifier;
            const json = JSON.stringify(this.shippingAddress);

            const response = await fetch("/ws/update-express-shipping-method", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: json
            });

            if (!response?.ok) {
                // Http error, tell Adyen that we don't support this shipping method.
                IssueLogger.log("Rejecting due to network error.", "onApplePayShippingMethodSelected", response.statusText);
                alreadyRejected = true;
                this.rejectShippingMethod(reject);
            }

            const shippingMethodUpdate: IApplePayShippingMethodUpdate = await response.json();
            const appleShippingMethods = this.mapShippingMethodUpdateToApple(shippingMethodUpdate);
            resolve(appleShippingMethods);
        } catch (error: unknown) {
            if (!alreadyRejected) { // A rejection above may cause an exception, so don't reject or log again.
                IssueLogger.log("onApplePayShippingMethodSelected", "Express-Pay", error);
                this.rejectShippingMethod(reject);
            }
        }
    }

    private mapShippingMethodUpdateToApple(shippingMethodUpdate: IApplePayShippingMethodUpdate): ApplePayJS.ApplePayShippingMethodUpdate {
        return {
            newLineItems: shippingMethodUpdate
                .newLineItems
                .map(item => {
                    return {
                        amount: item.amount,
                        label: item.label,
                        type: "final",
                    }
                }),
            newTotal: {
                amount: shippingMethodUpdate.newTotal.amount,
                label: shippingMethodUpdate.newTotal.label,
                type: "final",
            },
        };
    }

    /**
     * A belt and braces approach to rejecting an Apple shipping method update when we don't have the
     * data for the rejection. For example, when we receive a network error.
     * 
     * We need to send these fields or we generate more logs but mostly they are just placeholders.
     * @param reject
     */
    private rejectShippingMethod(reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void): void {
        // We have to cast this after creation as the type definition insists on a newTotal field, but passing
        // that as 0 allows the order to go through.
        const applePayUpdate = {
            errors: this.createAppleUiErrors([{ code: "unknown", message: "Error with shipping method" }])
        } as ApplePayJS.ApplePayShippingContactUpdate;

        return reject(applePayUpdate);
    }

    private postError(context: string, error: unknown, paymentProvider: "Apple" | "Google"): void {
        this.showPaymentFailedModal(`Sorry, we could not contact ${paymentProvider}. This may only be temporary.`);
        IssueLogger.log(context, "Express-Pay", error);
    }

    private showPaymentFailedModal(errorMessage: string): void {
        let errorMessageElement = document.getElementById("failed-payment-message");
        errorMessageElement.innerHTML = errorMessage;

        let errorModal = <HTMLDivElement>document.getElementById("failed-payment-modal");
        window.Modal.OpenModal(errorModal, () => FormSubmitToggle.EnableForm(this.formSelector));

        // Scroll to the top so the failure can be seen, otherwise it might stay out of sight on thin displays.
        window.scrollTo(0, 0);
    }

    private onCompletePaymentResponseReceived(response: IApplePayPaymentResponse, actions: any): void {
        try {
            this.idempotencyKey = response.idempotencyKey;

            if (response.success) {
                const { resultCode, action, order } = response.clientData;
                actions.resolve({ resultCode, action, order });

                window.location.href = response.mainRedirectUrl;
            }
            else if (response.errors && response.errors.length > 0) {
                let applePayPaymentAuthorizationResult: ApplePayJS.ApplePayPaymentAuthorizationResult = {
                    errors: this.createAppleUiErrors(response.errors),
                    status: ApplePaySession.STATUS_FAILURE
                }
                // Rejecting the payment SHOULD show the errors in the payment sheet but it is closed for some reason.
                actions.reject(applePayPaymentAuthorizationResult);

                // To show the errors for now, we use our modal instead.
                let html = "<ul>";

                for (const err of response.errors) {
                    html += "<li>" + err.message + "</li>";
                }
                html += "</ul>";

                this.showPaymentFailedModal("We received an error when processing your payment." + html);
            }
            else {
                // This has the potential to be a normal response for 'no funds'. If so then we need to show a more specific error to the user and log as an error for us.
                IssueLogger.log("Apple Pay failed to authorise but did not return any errors.", "onCompletePaymentResponseReceived");
                this.showPaymentFailedModal("We received an error when processing your payment.");

                actions.reject();
            }
        } catch (e: unknown) {
            IssueLogger.log("Apple Pay failed to authorise and returned an error.", "onCompletePaymentResponseReceived", e);
            this.showPaymentFailedModal("We received an error when processing your payment.");

            actions.reject();
        }
    }

    private onGooglePayExpressCompletePaymentResponseReceived(response: IGooglePayPaymentResponse, actions: SubmitActions): void {
        try {
            this.idempotencyKey = response.idempotencyKey;

            if (response.success) {

                if (response.mainRedirectUrl) {
                    if (actions && response.clientData) {
                        const { resultCode, action, order } = response.clientData;
                        actions.resolve({ resultCode, action, order });
                    }
                    window.location.href = response.mainRedirectUrl;
                }

                else if (response.clientData) {
                    if (response.clientData.resultCode == "IdentifyShopper" ||
                        response.clientData.resultCode == "ChallengeShopper") {
                        if (actions) {
                            actions.resolve({ resultCode: response.clientData.resultCode });
                        }
                        this.showThreeDs2Challenge(response.clientData.action);
                    }
                    else if (response.clientData.resultCode == "Refused") {
                        const error = this.createGooglePayAuthError("Refused", "PAYMENT_AUTHORIZATION",
                            "PAYMENT_DATA_INVALID", "Your bank has declined payment.");
                        actions?.reject(error);
                    }
                }
            }
            else if (response.errors && response.errors.length > 0) {
                const firstError = response.errors[0];
                const error = this.createGooglePayAuthError("Refused", "PAYMENT_AUTHORIZATION",
                    "PAYMENT_DATA_INVALID", firstError.message);
                actions.reject(error);
            }
            else {
                const error = this.createGooglePayAuthError("Error", "PAYMENT_AUTHORIZATION",
                    "PAYMENT_DATA_INVALID", "We could not process your payment, please check that your payment method is still valid.");
                actions.reject(error);
            }
        } catch (e: unknown) {
            IssueLogger.log("Google Pay failed to authorise and returned an error.", "onGooglePayExpressCompletePaymentResponseReceived", e);
            const error = this.createGooglePayAuthError("Error", "PAYMENT_AUTHORIZATION",
                "OTHER_ERROR", "We received an error when processing your payment.");
            actions.reject(error);
        }
    }

    private showThreeDs2Challenge(actionJson: string): void {
        if (this.verificationComponent) {
            this.verificationComponent.unmount();
        }

        const threeDSConfiguration = {
            challengeWindowSize: "05"
            // Set to any of the following:
            // "02": ["390px", "400px"] -  The default window size
            // "01": ["250px", "400px"]
            // "03": ["500px", "600px"]
            // "04": ["600px", "400px"]
            // "05": ["100%", "100%"]
        }
        let action = JSON.parse(actionJson);
        this.verificationComponent = this.checkout.createFromAction(action, threeDSConfiguration);
        // When mounted this will call back to handleOnAdditionalDetails with the 3ds2 challenge result.
        this.verificationComponent.mount("#" + this.verificationContainerId);

        // Show the 3ds2 UI in a modal display.
        let modalDivElement = <HTMLDivElement>document.getElementById("verification-3ds2-modal");
        window.Modal.OpenModal(modalDivElement, () => {
            // Create a new key as the previous one is now used and will generate an error on an attempted re-use.
            this.idempotencyKey = Date.now().toString();
            document.getElementById(this.verificationContainerId).innerHTML = "";
            this.verificationComponent.unmount();
            this.verificationComponent = null;
            window.FormSubmitToggle.EnableForm(this.formSelector);
        });
    }

    private createGooglePayAuthError(resultCode: ResultCode, intent: CallbackIntent, reason: ErrorReason, message: string): CheckoutAdvancedFlowResponse {
        return {
            resultCode,
            error: {
                googlePayError: { intent, reason, message }
            }
        };
    }

    private googleNetworkError(intent: CallbackIntent, reason: ErrorReason): google.payments.api.PaymentDataRequestUpdate {
        return {
            error: {
                intent, reason, message: "Connection error, unable to request new shipping options",
            },
        };
    }

    /**
     * 
     * @param state
     * @param component
     * @param actions - Can be null if the payment is frictionless. This is passed in from Adyen.
     */
    public handleOnAdditionalDetails(state: AdditionalDetailsData, component, actions?): void {
        let postData = {
            idempotencyKey: this.idempotencyKey,
            threeDSResult: state.data.details.threeDSResult,
            paymentData: component.props.paymentData,
        };

        let jsonPost = JSON.stringify(postData);
        window.HttpRequest.PostJson('/checkout/validate-3ds2', jsonPost,
            (jsonResponse) => { this.on3ds2ResponseReceived(jsonResponse, actions); },
            (jsonResponse) => {
                this.postError("handleOnAdditionalDetails", jsonResponse, "Google"); actions?.reject()
            });
    }

    private on3ds2ResponseReceived(json: string, actions?): void {
        let initiateResponse = <I3ds2Response>JSON.parse(json);
        let paymentsResponse = initiateResponse.adyenResponse;

        if (paymentsResponse) {
            // 3ds2 payment flow finished, present the result.
            if (initiateResponse.mainRedirectUrl) {
                if (actions) { // Some 3ds2 cards are 'frictionless' and do not require user interaction, plus they won't have actions.
                    const { resultCode, action, order } = initiateResponse.clientData;
                    actions.resolve({ resultCode, action, order });
                }
                window.location.href = initiateResponse.mainRedirectUrl;
            }
            else if (paymentsResponse.resultCode === "Refused") {
                this.showPaymentFailedModal("Your bank has declined payment.<br />");
                this.idempotencyKey = initiateResponse.idempotencyKey;
                actions?.reject();
                //this.turnstileWrapper.Reset();
            }
            else {
                this.showPaymentFailedModal("We received an error when processing your payment.");
                // This should have been logged on the server. Logging it here as well would tell us if it was a real client error or someone fiddling with the API.
                window.IssueLogger.log("3DS2 failed", "On3DS2ResultCode", paymentsResponse.resultCode);
                this.idempotencyKey = initiateResponse.idempotencyKey;
                //this.turnstileWrapper.Reset();
                actions?.reject();
            }
        }
        else {
            if (initiateResponse.mainRedirectUrl) {
                // If the server does not have a cart (due to order already being paid) then it will redirect us.
                window.location.href = initiateResponse.mainRedirectUrl;
                actions?.resolve();
                return;
            }
            // This will happen with network issues connecting to the payment provider and is logged on the server.
            this.showPaymentFailedModal("Sorry, we could not process your payment.");
            this.idempotencyKey = initiateResponse.idempotencyKey;
            //this.turnstileWrapper.Reset();
            actions?.reject();
        }
    }
}
